import { useState, type SyntheticEvent } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { ApiError } from "../../../../api/common";
import {
  useConfig,
  useCreateImport,
  useUpdateImport,
  useUploadImportDiffById,
} from "../../../../api/queries";
import { ImportErrorModal } from "../../../../components/ImportErrorModal";
import { ImportDiffModal } from "../../../../components/ImportDiffModal";
import { InvoicePickerField } from "../../../../components/EntityPickerField";
import type {
  ImportDiffDto,
  ImportItem,
  ImportResultDto,
  ImportValidationIssue,
  InvoiceId,
} from "../../../../api/types";

type ConflictData = {
  firstTransactionDate: string;
  lastTransactionDate: string;
  conflictingImports: ImportItem[];
};

export type NewImportModalProps = {
  open: boolean;
  onClose: () => void;
  /**
   * Called once the import flow is done. `result` is set only when the upload
   * was saved directly; a merge into an existing import (whose review dialog
   * may also have been discarded or left for later) calls this without one.
   */
  onImported?: (result?: ImportResultDto) => void;
  /**
   * Preloaded upload, for callers that build the file themselves — the
   * import-from-invoice flow parses the PDF in the browser and hands the
   * resulting CSV straight to this modal, so the whole overlap → update →
   * review chain below works unchanged. Mount with a `key` to reset.
   */
  initialFile?: File | null;
  initialImportType?: string;
  initialInvoiceId?: InvoiceId | null;
  /**
   * Mark the import as filled from the period's invoice, closing it to later
   * statement uploads.
   */
  finalize?: boolean;
  /** Shown above the form; explains a preloaded upload. */
  intro?: React.ReactNode;
};

export const NewImportModal = ({
  open,
  onClose,
  onImported,
  initialFile = null,
  initialImportType = "",
  initialInvoiceId = null,
  finalize = false,
  intro,
}: NewImportModalProps) => {
  const { data: config } = useConfig();
  const createImport = useCreateImport();
  const uploadDiff = useUploadImportDiffById();
  const updateImport = useUpdateImport();

  const [selectedImport, setSelectedImport] =
    useState<string>(initialImportType);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceId | null>(
    initialInvoiceId,
  );
  const [file, setFile] = useState<File | null>(initialFile);
  const [status, setStatus] = useState<
    "idle" | "uploading" | "success" | "error"
  >("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    ImportValidationIssue[]
  >([]);
  const [validationAttempt, setValidationAttempt] = useState(0);
  const [conflictData, setConflictData] = useState<ConflictData | null>(null);
  // Rows the backend dropped for belonging to a period already finalized from
  // its invoice — a couple at a period boundary is normal, but say so rather
  // than quietly importing fewer rows than the file held.
  const [skippedFinalized, setSkippedFinalized] = useState(0);
  // Incoming legs dropped as the mirror image of an outgoing row — one
  // transfer between two own accounts, which the export writes twice.
  const [mergedTransfers, setMergedTransfers] = useState(0);
  // Escape hatch: acknowledge the overlap is genuinely separate data before the
  // low-emphasis "Import as new" action unlocks.
  const [ackNewImport, setAckNewImport] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  // When set, the picked file has been diffed against an existing import and the
  // merge/review preview takes over the modal.
  const [mergeTarget, setMergeTarget] = useState<{
    item: ImportItem;
    diff: ImportDiffDto;
  } | null>(null);

  const activeImportConfig = config?.importConfigs?.find(
    (c) => c.name === selectedImport,
  );
  const activeFormat = activeImportConfig?.format;
  // Importers flagged `has_invoice = false` (e.g. plain account statements)
  // shouldn't offer an invoice — hide the picker. Default to showing it while
  // no type is selected or config is still loading.
  const showInvoicePicker = activeImportConfig?.hasInvoice ?? true;

  const doImport = (force: boolean) => {
    if (!file || !selectedImport) return;

    setStatus("uploading");
    setErrorMessage(null);
    setValidationErrors([]);
    setConflictData(null);
    setAckNewImport(false);
    setUpdateError(null);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("importType", selectedImport);
    if (showInvoicePicker && selectedInvoice) {
      formData.append("invoice", selectedInvoice);
    }
    if (finalize) {
      formData.append("finalize", "true");
    }

    createImport.mutate(
      { force, formData },
      {
        onSuccess: (result) => {
          setSkippedFinalized(result.skippedFinalized);
          setMergedTransfers(result.mergedTransfers);
          setStatus("success");
          onImported?.(result);
        },
        onError: (error) => {
          if (error instanceof ApiError) {
            if (error.data.type === "importConflict") {
              setConflictData({
                firstTransactionDate: error.data.firstTransactionDate,
                lastTransactionDate: error.data.lastTransactionDate,
                conflictingImports: error.data.conflictingImports,
              });
              setStatus("idle");
              return;
            }
            if (error.data.type === "importValidation") {
              setValidationErrors(error.data.issues);
              setValidationAttempt((n) => n + 1);
              setStatus("idle");
              return;
            }
            setErrorMessage(error.message || "Import failed.");
          } else {
            console.error("Upload failed", error);
            setErrorMessage(String(error));
          }
          setStatus("error");
        },
      },
    );
  };

  const handleSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    doImport(false);
  };

  // Merge the picked file into an existing overlapping import: upload it as a
  // diff against that import, then open the same review/apply preview as the
  // "Upload new version" flow — handled here so every caller of this modal gets
  // the behaviour without wiring it up.
  const handleUpdateExisting = (target: ImportItem) => {
    if (!file || uploadDiff.isPending) return;
    setUpdateError(null);
    uploadDiff.mutate(
      { importId: target.id, file },
      {
        onSuccess: (diff) => setMergeTarget({ item: target, diff }),
        onError: (error) =>
          setUpdateError(
            error instanceof ApiError
              ? error.message
              : `Could not prepare the update: ${String(error)}`,
          ),
      },
    );
  };

  // The merge preview closes on apply, discard, or "Later" (which leaves the
  // update pending on the import) — the New Import flow is done either way, so
  // tear it down and refresh.
  const closeMerge = () => {
    setMergeTarget(null);
    setConflictData(null);
    onImported?.();
    onClose();
  };

  const closeConflict = () => {
    setConflictData(null);
    setAckNewImport(false);
    setUpdateError(null);
  };

  const canSubmit = !!file && !!selectedImport && status !== "uploading";

  return (
    <>
      <Dialog
        open={open && !mergeTarget}
        onClose={status === "uploading" ? undefined : onClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Import Transactions</DialogTitle>
        <DialogContent>
          {intro}
          {status === "success" && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Import successful!
              {skippedFinalized > 0 &&
                (skippedFinalized === 1
                  ? " 1 transaction was skipped: it falls inside a period already finalized from its invoice, which is the complete list for that period."
                  : ` ${skippedFinalized} transactions were skipped: they fall inside a period already finalized from its invoice, which is the complete list for that period.`)}
              {mergedTransfers > 0 &&
                (mergedTransfers === 1
                  ? " 1 internal transfer was merged: the file listed it once from each account, and only the outgoing row is kept."
                  : ` ${mergedTransfers} internal transfers were merged: the file listed each once from each account, and only the outgoing row is kept.`)}
            </Alert>
          )}
          {status === "error" && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage ?? "Import failed. Please try again."}
            </Alert>
          )}
          <Box
            component="form"
            id="new-import-form"
            onSubmit={handleSubmit}
            sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 1 }}
          >
            <FormControl fullWidth>
              <InputLabel id="new-import-type-label">Import Type</InputLabel>
              <Select
                labelId="new-import-type-label"
                value={selectedImport}
                label="Import Type"
                onChange={(e) => setSelectedImport(e.target.value)}
                disabled={!config}
              >
                {config?.importConfigs?.map((option) => (
                  <MenuItem key={option.name} value={option.name}>
                    {option.name} ({option.format})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {showInvoicePicker && (
              <InvoicePickerField
                accountInvoicesOnly
                label="Invoice (Optional)"
                value={selectedInvoice}
                onChange={setSelectedInvoice}
              />
            )}

            <Button variant="outlined" component="label" fullWidth>
              {file ? file.name : "Select File"}
              <input
                type="file"
                hidden
                onChange={(e) =>
                  setFile(e.target.files?.[0] ? e.target.files[0] : null)
                }
                accept={activeFormat ? `.${activeFormat}` : undefined}
              />
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={status === "uploading"}>
            {status === "success" ? "Close" : "Cancel"}
          </Button>
          <Button
            type="submit"
            form="new-import-form"
            variant="contained"
            color="primary"
            disabled={!canSubmit}
          >
            {status === "uploading" ? "Importing…" : "Import"}
          </Button>
        </DialogActions>
      </Dialog>

      <ImportErrorModal
        key={validationAttempt}
        issues={validationErrors}
        onClose={() => setValidationErrors([])}
        onRetry={() => {
          setValidationErrors([]);
          doImport(false);
        }}
      />

      <Dialog
        open={conflictData !== null && !mergeTarget}
        onClose={uploadDiff.isPending ? undefined : closeConflict}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>This looks like a re-import</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            The file you are importing covers{" "}
            <strong>{conflictData?.firstTransactionDate}</strong> to{" "}
            <strong>{conflictData?.lastTransactionDate}</strong>, which overlaps{" "}
            {conflictData?.conflictingImports.length === 1
              ? "an import you already have"
              : `${conflictData?.conflictingImports.length} imports you already have`}
            . Update the existing import to preview exactly what's added,
            removed, or settled before anything is saved — usually what you want
            when re-importing.
          </Typography>

          <Stack spacing={1.5} sx={{ mt: 2 }}>
            {conflictData?.conflictingImports.map((imp) => {
              const thisPending =
                uploadDiff.isPending &&
                uploadDiff.variables?.importId === imp.id;
              return (
                <Box
                  key={imp.id}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    p: 1.5,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {imp.importerName}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    {imp.firstTransactionDate} – {imp.lastTransactionDate}
                  </Typography>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={() => handleUpdateExisting(imp)}
                    disabled={uploadDiff.isPending}
                    startIcon={
                      thisPending ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : undefined
                    }
                  >
                    {thisPending ? "Preparing preview…" : "Update this import"}
                  </Button>
                </Box>
              );
            })}
          </Stack>

          {updateError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {updateError}
            </Alert>
          )}

          <Divider sx={{ my: 2 }} />

          <FormControlLabel
            control={
              <Checkbox
                checked={ackNewImport}
                onChange={(e) => setAckNewImport(e.target.checked)}
                disabled={uploadDiff.isPending}
              />
            }
            label={
              <Typography variant="body2">
                This is separate data, not a re-import
              </Typography>
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConflict} disabled={uploadDiff.isPending}>
            Cancel
          </Button>
          <Button
            color="inherit"
            onClick={() => doImport(true)}
            disabled={
              !ackNewImport || uploadDiff.isPending || status === "uploading"
            }
          >
            Import as new
          </Button>
        </DialogActions>
      </Dialog>

      {mergeTarget && (
        <ImportDiffModal
          open
          importId={mergeTarget.item.id}
          preloadedDiff={mergeTarget.diff}
          onApplied={() => {
            // Only now are the invoice's rows actually in the import. Link the
            // invoice to it — which also stamps every transaction of the period
            // with it — and close the period to further statement uploads.
            // Two calls because the endpoint applies one field per request.
            if (!finalize) return;
            void (async () => {
              if (selectedInvoice) {
                await updateImport.mutateAsync({
                  importId: mergeTarget.item.id,
                  body: { invoiceId: selectedInvoice },
                });
              }
              await updateImport.mutateAsync({
                importId: mergeTarget.item.id,
                body: { finalized: true },
              });
            })();
          }}
          onClose={closeMerge}
        />
      )}
    </>
  );
};
